import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { BrandSelect } from '../lib/brands.js';
import { dateTime } from '../lib/format.js';
import {
  OPEN_AUTO_LABEL,
  OPEN_HINT,
  OPEN_HUMAN_LABEL,
  OUTCOME_LABEL,
  REASON_LABEL,
  label,
} from '../lib/labels.js';
import type { ReactionsPoll } from '../lib/labels.js';
import { hrefFor } from '../lib/router.js';
import { useMe } from '../lib/session.js';
import type { Me } from '../lib/session.js';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  Loading,
  PageHeader,
  Panel,
} from '../components/ui.js';
import type { Tone } from '../components/ui.js';
import { Badge as Pill } from '../components/2b-status.js';
import { SendTarget } from '../components/2b-sendtarget.js';
import '../screens-2b.css';
import { JourneySim } from './JourneySim.js';
import { TrialChecklist } from './TrialChecklist.js';

interface TestItem {
  templateKey: string;
  code: string;
  name: string;
  brand: string;
  language: string;
  channel: 'service' | 'marketing';
  path: string | null;
  status: 'published' | 'uploaded';
  merged: boolean;
  hubSends: boolean;
  testable: boolean;
  reason: string | null;
  lastRun: { status: string; at: string } | null;
}

interface Overview {
  mode: 'dry_run' | 'test_allowlist';
  recipients: Array<{ address: string; masked: string }>;
  hourlyCap: number;
  usedLastHour: number;
  delayMs: number;
  items: TestItem[];
  missing: Array<{ code: string; brand: string; language: string }>;
}

interface ProviderStatus {
  provider: 'smartemailing' | 'mock';
  configured: boolean;
  source: 'db' | 'env' | 'none';
  sendMode: 'dry_run' | 'test_allowlist';
  allowlistCount: number;
  recipientsMasked: string[];
  blocked: boolean;
  reactionsPoll: ReactionsPoll | null;
}

const PROVIDER_LABEL: Record<string, string> = {
  smartemailing: 'SmartEmailing',
  mock: 'Zkušební provider',
};

interface Run {
  id: string;
  brand: string;
  code: string;
  language: string;
  templateKey: string;
  path: string;
  mode: string;
  recipientMasked: string;
  status: 'sent' | 'failed' | 'dry_run' | 'skipped';
  providerMessageId: string | null;
  provider: string | null;
  error: string | null;
  subject: string | null;
  createdAt: string;
  reactions: Record<string, string | null>;
  reactionState: 'na' | 'pending' | 'matched' | 'unmatched';
}

interface FlowCode {
  code: string;
  name: string;
  channel: string;
  testable: boolean;
  reason: string | null;
  summary: string | null;
  delayMinutes: number | null;
}

interface TraceStep {
  node: string;
  title: string;
  status: 'ok' | 'stopped' | 'pending' | 'skipped';
  detail: string;
  data?: Record<string, unknown>;
}

interface Trace {
  code: string;
  brand: string;
  mode: string;
  contactId: string | null;
  contactEmailMasked: string;
  messageId: string | null;
  steps: TraceStep[];
  stoppedAt: string | null;
  result: 'sent' | 'dry_run' | 'stopped' | 'waiting';
}

const PATH_LABEL: Record<string, string> = {
  service_transactional: 'Servis: hotové HTML (transakční)',
  marketing_bulk: 'Marketing: šablona nahraná ve SmartEmailingu',
  marketing_list_trigger: 'Marketing: spouštěč seznamu (nezkouší se)',
};

const RUN_STATUS: Record<Run['status'], { tone: Tone; text: string }> = {
  sent: { tone: 'ok', text: 'Odesláno' },
  failed: { tone: 'bad', text: 'Selhalo' },
  dry_run: { tone: 'neu', text: 'Nanečisto' },
  skipped: { tone: 'warn', text: 'Přeskočeno' },
};

/** Uzly obrazovky Schéma toku (pojmenování kroků stopy). */
const NODE_LABEL: Record<string, string> = {
  src_eshop: 'E-shop',
  ingest: 'Příjem událostí',
  journeys: 'Cesty zákazníků',
  planner: 'Plánovač',
  decisions: 'Rozhodnutí',
  template: 'Šablona',
  send: 'Odeslání',
  se: 'SmartEmailing',
  react: 'Reakce',
};

const STEP_TONE: Record<TraceStep['status'], Tone> = {
  ok: 'ok',
  stopped: 'bad',
  pending: 'warn',
  skipped: 'neu',
};
const STEP_TEXT: Record<TraceStep['status'], string> = {
  ok: 'v pořádku',
  stopped: 'zde se zastavilo',
  pending: 'čeká',
  skipped: 'přeskočeno',
};

/** Co s tím, když se test toku zastaví z obvyklého důvodu. */
const REASON_HINT: Record<string, string> = {
  frequency_cap:
    'Tomuto kontaktu už dnes odešel jiný marketing (například zkušební M01 z demo dat). Pro opakované zkoušky nastavte u značky frekvenční limit na 0 dní (Nastavení → Značky) nebo počkejte do dalšího dne; potom klikněte na „Pokračovat“.',
  template_not_synced:
    'Marketingová šablona není nahraná do SmartEmailingu. V tabulce nahoře u jejího řádku klikněte „Poslat“ (nahraje ji) nebo ji znovu publikujte; potom klikněte na „Pokračovat“.',
  template_missing:
    'Pro tento kód a jazyk není publikovaná šablona (ani anglická). Publikujte ji v záložce Šablony.',
  switch_off: 'Vypínač „smí odejít“ pro tento kód je vypnutý (záložka Přehled zpráv).',
};

const REACTION_LABEL: Record<string, string> = {
  delivered: 'doručeno',
  opened: 'otevřeno',
  clicked: 'kliknuto',
  bounced: 'odskočeno',
  unsubscribed: 'odhlášeno',
  complained: 'stížnost',
};

function Reactions({ run }: { run: Run }): JSX.Element {
  if (run.reactionState === 'na') return <span className="note">—</span>;
  if (run.reactionState === 'pending') return <Pill tone="warn">čeká na reakci</Pill>;
  if (run.reactionState === 'unmatched') {
    return (
      <span title="Za půl hodiny se nepodařilo spárovat odeslání s událostí SmartEmailingu (značka, adresa, čas). Reakce se stahují každých 15 minut (Úlohy → Spustit teď); zkontrolujte i SmartEmailing.">
        <Pill tone="neu">nespárováno</Pill>
      </span>
    );
  }
  /* `opened_human` je první otevření člověkem; `opened` první jakékoli (i automatické). */
  const seen = Object.entries(run.reactions).filter(([type, at]) => at && type !== 'opened_human');
  return (
    <span className="row">
      {seen.map(([type, at]) => (
        <span key={type} title={type === 'opened' ? OPEN_HINT : dateTime(at)}>
          <Pill tone={type === 'bounced' || type === 'complained' ? 'bad' : 'ok'}>
            {type === 'opened'
              ? run.reactions.opened_human
                ? OPEN_HUMAN_LABEL
                : OPEN_AUTO_LABEL
              : (REACTION_LABEL[type] ?? type)}
          </Pill>
        </span>
      ))}
    </span>
  );
}

export function SendTests({ onOpenSent }: { onOpenSent?: () => void }): JSX.Element {
  const me = useMe();
  if (me.isLoading) return <Loading what="stav prostředí" />;
  if (me.isError || !me.data)
    return (
      <ErrorState
        title="Stav prostředí se nepodařilo načíst"
        error={me.error}
        onRetry={() => void me.refetch()}
      />
    );
  if (me.data.sendMode === 'live' || me.data.sendModeCeiling === 'live') {
    return (
      <>
        <PageHeader
          level={2}
          title="Test odesílání"
          description="Zkušební zprávy na zkušební adresy."
        />
        <div className="confirm dngc" role="alert">
          <b>
            <Icon name="alert" size={16} /> Jen v zkušebním prostředí
          </b>
          <span>
            Test odesílání a test toku fungují jen tam, kde se zprávy smějí posílat nanečisto nebo
            jen na zkušební adresy. Tohle prostředí je ostré, proto je funkce zavřená.
          </span>
        </div>
      </>
    );
  }
  return <SendTestsBody me={me.data} onOpenSent={onOpenSent} />;
}

/** Stav providera nahoře: bez napojeného SmartEmailingu je ostré zkušební odeslání zablokované. */
function ProviderBanner({ status }: { status: ProviderStatus | undefined }): JSX.Element | null {
  if (!status) return null;
  if (status.blocked) {
    return (
      <div className="confirm dngc" role="alert" data-testid="provider-banner">
        <b>
          <Icon name="alert" size={16} /> SmartEmailing není napojený — zkušební odeslání je
          zablokované
        </b>
        <span>
          Zprávy by se jen simulovaly a nic by nedorazilo.{' '}
          <a href={hrefFor('settings')}>Zadejte přístupy v Nastavení → Napojení</a>.
        </span>
      </div>
    );
  }
  if (status.provider === 'smartemailing') {
    return (
      <div className="confirm" role="status" data-testid="provider-banner">
        <span>
          <Pill tone="ok">SmartEmailing je napojený</Pill>{' '}
          <span className="note">
            {status.source === 'db' ? 'přístupy z Nastavení' : 'přístupy z prostředí'} ·{' '}
            {status.sendMode === 'dry_run' ? 'nanečisto' : 'jen zkušební adresy'} · zkušebních
            adres: {status.allowlistCount}
            {status.recipientsMasked.length > 0 ? ` (${status.recipientsMasked.join(', ')})` : ''}
          </span>
        </span>
        <div className="note" data-testid="reactions-poll">
          {status.reactionsPoll
            ? `Poslední stažení reakcí: ${dateTime(status.reactionsPoll.at)}${
                status.reactionsPoll.read === null
                  ? ''
                  : `, ${status.reactionsPoll.read} událostí (${status.reactionsPoll.stored ?? 0} nových)`
              }${status.reactionsPoll.error ? ' — s chybou, viz Úlohy' : ''}`
            : 'Reakce se ještě nestahovaly (úloha běží každých 15 minut; Úlohy → Spustit teď).'}
        </div>
      </div>
    );
  }
  return (
    <div className="confirm" role="status" data-testid="provider-banner">
      <span>
        <Pill tone="neu">Nanečisto: SmartEmailing se nevolá</Pill>{' '}
        <span className="note">SmartEmailing zatím není napojený, nic neodchází ven.</span>
      </span>
    </div>
  );
}

function SendTestsBody({ me, onOpenSent }: { me: Me; onOpenSent?: () => void }): JSX.Element {
  const client = useQueryClient();
  const [recipient, setRecipient] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [brandFilter, setBrandFilter] = useState('');
  const [showMissing, setShowMissing] = useState(false);
  /* Co čeká na potvrzení: jedna zkušební zpráva, nebo všechny najednou. */
  const [ask, setAsk] = useState<{ kind: 'one'; item: TestItem } | { kind: 'all' } | null>(null);

  const overview = useQuery<Overview>({
    queryKey: ['send-tests'],
    queryFn: () => api.get('/api/send-tests'),
    refetchInterval: 30_000,
  });
  const status = useQuery<ProviderStatus>({
    queryKey: ['send-tests-status'],
    queryFn: () => api.get('/api/send-tests/status'),
    refetchInterval: 30_000,
  });
  const runs = useQuery<{ items: Run[] }>({
    queryKey: ['send-test-runs'],
    queryFn: () => api.get('/api/send-tests/runs?limit=100'),
    refetchInterval: 15_000,
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['send-tests'] });
    void client.invalidateQueries({ queryKey: ['send-tests-status'] });
    void client.invalidateQueries({ queryKey: ['send-test-runs'] });
  };

  const recipients = overview.data?.recipients ?? [];
  const chosen = recipient || (recipients.length === 1 ? (recipients[0]?.address ?? '') : '');

  const sendOne = useMutation({
    mutationFn: (templateKey: string) =>
      api.post<{ run: Run }>('/api/send-tests/send', {
        templateKey,
        ...(chosen ? { recipient: chosen } : {}),
      }),
    onSuccess: ({ run }) => {
      setError(null);
      setInfo(
        run.status === 'sent'
          ? `Odesláno přes ${PROVIDER_LABEL[run.provider ?? ''] ?? 'neznámý provider'} (${run.code}, ${run.brand}/${run.language}) na ${run.recipientMasked}.`
          : run.status === 'dry_run'
            ? `Nanečisto: ${run.code} se vykreslila, nic se neposlalo.`
            : `Neodešlo: ${run.error ?? run.status}`,
      );
      refresh();
    },
    onError: (err: Error) => {
      setInfo(null);
      setError(err.message);
    },
  });

  const sendAll = useMutation({
    mutationFn: () =>
      api.post<{ runs: Run[]; stoppedBecause: string | null }>('/api/send-tests/send-all', {
        ...(chosen ? { recipient: chosen } : {}),
        ...(brandFilter ? { brand: brandFilter } : {}),
      }),
    onSuccess: (res) => {
      setError(null);
      const sent = res.runs.filter((r) => r.status === 'sent').length;
      const failed = res.runs.filter((r) => r.status === 'failed').length;
      setInfo(
        `Hotovo: odesláno ${sent}, selhalo ${failed}, přeskočeno ${res.runs.filter((r) => r.status === 'skipped').length}.` +
          (res.stoppedBecause === 'provider_failure'
            ? ' Po prvním selhání se zbytek zastavil (jistič).'
            : res.stoppedBecause === 'hourly_cap'
              ? ' Dosažen hodinový strop.'
              : ''),
      );
      refresh();
    },
    onError: (err: Error) => {
      setInfo(null);
      setError(err.message);
    },
  });

  if (overview.isLoading) return <Loading what="zkušební odesílání" />;
  if (overview.isError)
    return (
      <ErrorState
        title="Zkušební odesílání se nepodařilo načíst"
        error={overview.error}
        onRetry={() => void overview.refetch()}
      />
    );
  const data = overview.data;
  if (!data) return <Loading />;

  const items = data.items.filter((i) => !brandFilter || i.brand === brandFilter);
  const testable = items.filter((i) => i.testable);
  const needsChoice = recipients.length > 1 && !chosen;
  const dry = data.mode === 'dry_run';
  const busy = sendOne.isPending || sendAll.isPending;

  return (
    <>
      <PageHeader
        level={2}
        title="Test odesílání"
        description="Pošle ukázkovou zprávu z publikované šablony na zkušební adresu, ať uvidíte, jak e-mail doopravdy dorazí. Zákazníkům nikdy nic neodejde."
      />
      <SendTarget
        me={me}
        what={
          recipients.length > 0
            ? `Zprávy půjdou jen na ${recipients.length === 1 ? 'tuto zkušební adresu' : 'zkušební adresy'}: ${recipients.map((r) => r.masked).join(', ')}.`
            : 'Není nastavená žádná zkušební adresa, takže není komu poslat.'
        }
      />
      <ProviderBanner status={status.data} />
      <TrialChecklist />
      {error ? <ErrorState title="Test se nepovedl" error={new Error(error)} /> : null}
      {info ? (
        <div className="confirm" role="status">
          <span>{info}</span>
        </div>
      ) : null}

      <Panel
        title="Zkušební odeslání šablon"
        icon="flask"
        subtitle="Pošle ukázkovou zprávu z publikované šablony stejnou cestou jako ostrý provoz. Nezakládá kontakt a nezasahuje do souhlasů ani frekvenčního limitu."
        action={<BrandSelect value={brandFilter} onChange={setBrandFilter} shopOnly />}
      >
        <div className="row">
          <Pill tone={dry ? 'neu' : 'warn'}>
            {dry
              ? 'Nanečisto: nic se neposílá'
              : 'Zkušební režim: posílá se jen na zkušební adresy'}
          </Pill>
          <span className="note">
            Za poslední hodinu {data.usedLastHour} z {data.hourlyCap} zkušebních odeslání.
          </span>
        </div>
        <div className="row s2b-gap">
          <label>
            Příjemce{' '}
            {recipients.length === 0 ? (
              <span className="note">
                Seznam zkušebních adres je prázdný
                {dry ? ' (nanečisto to nevadí)' : ', takže není komu poslat'}.
              </span>
            ) : (
              <select
                value={chosen}
                onChange={(e) => setRecipient(e.target.value)}
                aria-label="Příjemce testu"
              >
                {recipients.length > 1 ? <option value="">— vyberte —</option> : null}
                {recipients.map((r) => (
                  <option key={r.address} value={r.address}>
                    {r.address}
                  </option>
                ))}
              </select>
            )}
          </label>
          <button
            type="button"
            className="btn"
            disabled={
              needsChoice || testable.length === 0 || (recipients.length === 0 && !dry) || busy
            }
            onClick={() => setAsk({ kind: 'all' })}
          >
            Poslat vše ({testable.length})
          </button>
          {needsChoice ? <span className="note">Nejdřív vyberte příjemce.</span> : null}
        </div>
      </Panel>

      <Panel
        title="Co jde vyzkoušet"
        subtitle="Každá publikovaná šablona: kód × značka × jazyk. Marketingová se před prvním odesláním sama nahraje do SmartEmailingu."
      >
        {items.length === 0 ? (
          <EmptyState title="Není co zkoušet">
            Zatím není publikovaná žádná šablona. Publikujte je v záložce Šablony.
          </EmptyState>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Kód</th>
                  <th>Značka / jazyk</th>
                  <th>Cesta</th>
                  <th>Stav šablony</th>
                  <th>Naposledy</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.templateKey}>
                    <td>
                      <b>{i.code}</b> <span className="note">{i.name}</span>
                      {i.merged ? <span className="note"> · souhrnná</span> : null}
                      <details className="s2b-tech">
                        <summary>technický klíč</summary>
                        <code>{i.templateKey}</code>
                      </details>
                    </td>
                    <td>
                      {i.brand} / {i.language}
                    </td>
                    <td>{i.path ? (PATH_LABEL[i.path] ?? i.path) : '—'}</td>
                    <td>
                      {i.channel === 'marketing' ? (
                        i.status === 'uploaded' ? (
                          <Pill tone="ok">nahraná do SmartEmailingu</Pill>
                        ) : (
                          <Pill tone="warn">publikovaná, nenahraná</Pill>
                        )
                      ) : (
                        <Pill tone="ok">publikovaná</Pill>
                      )}
                      {!i.hubSends ? (
                        <span className="note"> · v provozu posílá e-shop</span>
                      ) : null}
                    </td>
                    <td>
                      {i.lastRun ? (
                        <>
                          <Pill tone={RUN_STATUS[i.lastRun.status as Run['status']].tone}>
                            {RUN_STATUS[i.lastRun.status as Run['status']].text}
                          </Pill>{' '}
                          <span className="note">{dateTime(i.lastRun.at)}</span>
                        </>
                      ) : (
                        <span className="note">—</span>
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn"
                        disabled={!i.testable || busy || needsChoice}
                        aria-label={`Poslat zkušebně ${i.code} (${i.brand}/${i.language})`}
                        {...(i.reason || needsChoice
                          ? { title: i.reason ?? 'Nejdřív vyberte příjemce.' }
                          : {})}
                        onClick={() => setAsk({ kind: 'one', item: i })}
                      >
                        <Icon name="send" size={14} /> Poslat
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data.missing.length > 0 ? (
          <p className="note">
            <button className="btn" onClick={() => setShowMissing(!showMissing)}>
              {showMissing ? 'Skrýt' : 'Zobrazit'} chybějící šablony ({data.missing.length})
            </button>
          </p>
        ) : null}
        {showMissing ? (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Kód</th>
                  <th>Značka</th>
                  <th>Jazyk</th>
                  <th>Stav</th>
                </tr>
              </thead>
              <tbody>
                {data.missing
                  .filter((m) => !brandFilter || m.brand === brandFilter)
                  .map((m) => (
                    <tr key={`${m.code}|${m.brand}|${m.language}`}>
                      <td>{m.code}</td>
                      <td>{m.brand}</td>
                      <td>{m.language}</td>
                      <td>
                        <Pill tone="neu">chybí publikovaná šablona</Pill>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Panel>

      <Panel
        title="Výsledky"
        subtitle="Reakce (doručení, otevření, klik) přicházejí se stahováním událostí ze SmartEmailingu; tabulka se obnovuje sama."
        action={
          <button className="btn" onClick={refresh}>
            <Icon name="refresh" size={14} /> Obnovit
          </button>
        }
      >
        {runs.isLoading ? (
          <Loading what="výsledky" />
        ) : runs.isError ? (
          <ErrorState
            title="Výsledky se nepodařilo načíst"
            error={runs.error}
            onRetry={() => void runs.refetch()}
          />
        ) : (runs.data?.items ?? []).length === 0 ? (
          <EmptyState title="Zatím nic neodešlo">
            Po prvním zkušebním odeslání se tu objeví výsledek i reakce příjemce.
          </EmptyState>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Kdy</th>
                  <th>Kód</th>
                  <th>Značka / jazyk</th>
                  <th>Příjemce</th>
                  <th>Odeslal</th>
                  <th>Výsledek</th>
                  <th>Reakce</th>
                  <th>Číslo odeslání</th>
                </tr>
              </thead>
              <tbody>
                {(runs.data?.items ?? []).map((r) => (
                  <tr key={r.id}>
                    <td>{dateTime(r.createdAt)}</td>
                    <td>
                      <b>{r.code}</b>
                    </td>
                    <td>
                      {r.brand} / {r.language}
                    </td>
                    <td>{r.recipientMasked}</td>
                    <td>
                      {r.provider ? (
                        <Pill tone={r.provider === 'mock' ? 'warn' : 'ok'}>
                          {PROVIDER_LABEL[r.provider] ?? r.provider}
                        </Pill>
                      ) : (
                        <span className="note">—</span>
                      )}
                    </td>
                    <td>
                      <Pill tone={RUN_STATUS[r.status].tone}>{RUN_STATUS[r.status].text}</Pill>
                      {r.provider === 'mock' && r.status === 'sent' ? (
                        <div className="note">Jen simulace — nic neodešlo ven.</div>
                      ) : null}
                      {r.error ? <div className="note s2b-wrap">{r.error}</div> : null}
                    </td>
                    <td>
                      <Reactions run={r} />
                    </td>
                    <td className="note">{r.providerMessageId ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <FlowTest me={me} mode={data.mode} onOpenSent={onOpenSent} />
      <JourneySim me={me} />

      <ConfirmDialog
        open={ask !== null}
        title={
          ask?.kind === 'one'
            ? `Poslat zkušebně ${ask.item.code} (${ask.item.brand}/${ask.item.language})?`
            : `Poslat všech ${testable.length} zkušebních zpráv?`
        }
        confirmLabel={dry ? 'Spustit nanečisto' : 'Poslat'}
        busy={busy}
        onCancel={() => setAsk(null)}
        onConfirm={() => {
          if (ask?.kind === 'one') sendOne.mutate(ask.item.templateKey);
          else if (ask?.kind === 'all') sendAll.mutate();
          setAsk(null);
        }}
      >
        <p>
          {dry
            ? 'Nic neodejde, zpráva se jen vykreslí a zapíše jako zkouška.'
            : `Zpráva odejde na ${chosen || 'zkušební adresu'}. Zákazníkům nic nepůjde.`}
          {ask?.kind === 'all'
            ? ` Zprávy se pošlou popořadě s pauzou ${data.delayMs} ms a po prvním selhání se zbytek zastaví.`
            : ''}
        </p>
        <SendTarget me={me} compact />
      </ConfirmDialog>
    </>
  );
}

function FlowTest({
  me,
  mode,
  onOpenSent,
}: {
  me: Me;
  mode: Overview['mode'];
  onOpenSent?: () => void;
}): JSX.Element {
  const [askFlow, setAskFlow] = useState(false);
  const [brand, setBrand] = useState('TGP');
  const [code, setCode] = useState('S06');
  const [trace, setTrace] = useState<Trace | null>(null);
  const [error, setError] = useState<string | null>(null);

  const codes = useQuery<{ items: FlowCode[] }>({
    queryKey: ['send-test-flow-codes'],
    queryFn: () => api.get('/api/send-tests/flow/codes'),
    staleTime: 60_000,
  });

  const run = useMutation({
    mutationFn: (resume: boolean) =>
      api.post<{ trace: Trace }>('/api/send-tests/flow', { brand, code, resume }),
    onSuccess: (res) => {
      setError(null);
      setTrace(res.trace);
    },
    onError: (err: Error) => setError(err.message),
  });

  const items = codes.data?.items ?? [];
  const selected = items.find((c) => c.code === code);

  return (
    <Panel
      title="Test toku"
      icon="route"
      subtitle="Založí zkušebního zákazníka na první zkušební adrese, pošle události z e-shopu pro zvolený kód a provede plánování a rozhodování o zprávě jen pro něj. Stopa ukáže, kudy zpráva prošla a kde se zastavila."
    >
      {error ? <ErrorState title="Test toku se nepovedl" error={new Error(error)} /> : null}
      <div className="row">
        <BrandSelect value={brand} onChange={setBrand} allLabel={null} shopOnly />
        <select value={code} onChange={(e) => setCode(e.target.value)} aria-label="Kód zprávy">
          <optgroup label="Lze vyzkoušet">
            {items
              .filter((c) => c.testable)
              .map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} — {c.name}
                </option>
              ))}
          </optgroup>
          <optgroup label="Nelze (zatím)">
            {items
              .filter((c) => !c.testable)
              .map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} — {c.name}
                </option>
              ))}
          </optgroup>
        </select>
        <button
          type="button"
          className="btn pri"
          disabled={!selected?.testable || run.isPending}
          {...(selected?.reason ? { title: selected.reason } : {})}
          onClick={() => setAskFlow(true)}
        >
          Spustit test toku
        </button>
        <button
          className="btn"
          disabled={!selected?.testable || run.isPending}
          onClick={() => run.mutate(true)}
          title="Znovu spustí plánování a rozhodování nad posledním během (například po nahrání šablony nebo po uplynutí zpoždění)."
        >
          <Icon name="refresh" size={14} /> Pokračovat
        </button>
      </div>
      {selected ? (
        <p className="note">
          {selected.testable
            ? `${selected.summary ?? ''}${selected.delayMinutes ? ` Zpráva je podle katalogu splatná až ${selected.delayMinutes} min po události — pak použijte „Pokračovat“.` : ''}`
            : `Nelze vyzkoušet: ${selected.reason ?? ''}`}
        </p>
      ) : null}
      {codes.isLoading ? <Loading what="kódy" /> : null}
      <ConfirmDialog
        open={askFlow}
        title={`Spustit test toku pro ${code} (${brand})?`}
        confirmLabel="Spustit"
        busy={run.isPending}
        onCancel={() => setAskFlow(false)}
        onConfirm={() => {
          setAskFlow(false);
          run.mutate(false);
        }}
      >
        <p>
          {mode === 'dry_run'
            ? 'Založí se zkušební zákazník a zpráva se nanečisto projde celým tokem. Nic neodejde.'
            : 'Založí se zkušební zákazník a zpráva se opravdu odešle na první zkušební adresu. Zákazníkům nic nepůjde.'}
        </p>
        <SendTarget me={me} compact />
      </ConfirmDialog>

      {trace ? (
        <div className="trace" aria-label="Stopa toku">
          <div className="row">
            <Pill
              tone={
                trace.result === 'sent'
                  ? 'ok'
                  : trace.result === 'stopped'
                    ? 'bad'
                    : trace.result === 'dry_run'
                      ? 'neu'
                      : 'warn'
              }
            >
              {trace.result === 'sent'
                ? 'Odesláno'
                : trace.result === 'dry_run'
                  ? 'Nanečisto'
                  : trace.result === 'stopped'
                    ? `Zastaveno: ${NODE_LABEL[trace.stoppedAt ?? ''] ?? trace.stoppedAt}`
                    : 'Čeká'}
            </Pill>
            <span className="note">
              {trace.code} · {trace.brand} · {trace.contactEmailMasked}
            </span>
            {trace.contactId ? (
              <a href={hrefFor('customers', trace.contactId)}>Karta zákazníka</a>
            ) : null}
            {trace.messageId && onOpenSent ? (
              <button className="btn" onClick={onOpenSent}>
                Odeslané
              </button>
            ) : null}
          </div>
          <ol className="trace-steps">
            {trace.steps.map((s) => {
              const reason =
                s.node === 'decisions' && typeof s.data?.reason === 'string'
                  ? `${label(OUTCOME_LABEL, String(s.data.outcome))}: ${label(REASON_LABEL, s.data.reason)}`
                  : null;
              return (
                <li key={s.node} className={`trace-step ${s.status}`}>
                  <div className="row">
                    <b>{NODE_LABEL[s.node] ?? s.title}</b>
                    <Pill tone={STEP_TONE[s.status]}>{STEP_TEXT[s.status]}</Pill>
                  </div>
                  <p>{s.detail}</p>
                  {reason ? <p className="note">{reason}</p> : null}
                  {s.node === 'decisions' &&
                  typeof s.data?.reason === 'string' &&
                  REASON_HINT[s.data.reason] ? (
                    <p className="note">{REASON_HINT[s.data.reason]}</p>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </div>
      ) : null}
    </Panel>
  );
}
